/**
 * Where a number came from, one tap away (REQ-WEB-005, REQ-WEB-006).
 *
 * This is the product's whole claim made visible on the surface people
 * actually use. The detail page has shown citations since #34; the list — the
 * page everyone lands on — showed numbers with nothing behind them.
 *
 * Two things it must do that a tooltip cannot:
 *
 * * **Work by tap.** Phones are the primary shape (D7) and there is no hover
 *   there, so a hover-only affordance is the same as no affordance.
 * * **Mark disagreement before it is opened.** REQ-WEB-006 wants a cell with
 *   competing accepted values *marked*, not merely explorable. A marker you
 *   have to go looking for is not a marker, which is why the list payload
 *   carries `competing` rather than the client discovering it on open.
 *
 * Sources are fetched by id and cached for the life of the page. A list of
 * fifty lenses citing one manufacturer page is fifty references to one source;
 * fetching it fifty times would be rude to our own server and slow for no
 * reason.
 */
import { useEffect, useState } from "preact/hooks";

import type { Attribute, Source } from "./api";
import { citationHref, citedValueText, NOT_APPLICABLE } from "./format";

const cache = new Map<number, Promise<Source>>();

export function loadSource(id: number): Promise<Source> {
  let p = cache.get(id);
  if (!p) {
    p = fetch(`/api/v1/sources/${id}`).then((r) => {
      if (!r.ok) throw new Error(`source ${id}: ${r.status}`);
      return r.json() as Promise<Source>;
    });
    cache.set(id, p);
  }
  return p;
}

const SOURCE_TYPE: Record<string, string> = {
  manufacturer: "Manufacturer",
  manual: "Manual",
  press_release: "Press release",
  retailer: "Retailer",
  review: "Review",
  measured: "Measured",
  other: "Other source",
};

/** The one thing every failed load says: no codes, no status numbers (#213). */
export const LOAD_FAILED = "Couldn't load this. Please try again.";

/**
 * What a citation calls its source (#213, owner 2026-10-03). A value that
 * lens-specs worked out itself has the site as its publisher, and "Other" told
 * a reader nothing: it reads "Worked out by lens-specs". Decided on the
 * publisher, so it needs no source kind of its own.
 */
export function isOwnSource(s: Pick<Source, "publisher">): boolean {
  return (s.publisher ?? "").trim().toLowerCase() === "lens-specs";
}

export const OWN_SOURCE_LABEL = "Worked out by lens-specs";

/** The kind of source, in words: "Manufacturer", "Other source", or the site's own line. */
export function sourceKindLabel(s: Pick<Source, "publisher" | "source_type">): string {
  return isOwnSource(s) ? OWN_SOURCE_LABEL : (SOURCE_TYPE[s.source_type] ?? s.source_type);
}

interface HistoryValue {
  id: number;
  value: number | string | boolean | null;
  unit: string | null;
  submitted_value: number | string | boolean | null;
  submitted_unit: string | null;
  status: string;
  is_preferred: boolean;
  /** A source's statement that the spec does not apply to this lens (D45). */
  not_applicable?: boolean;
  source: Source | null;
}

/** The citation behind one cell, and every competing value if there are any. */
/**
 * The source of a value, named as soon as a keyboard user lands on it
 * (REQ-WEB-005: "clicking or FOCUSING a cell MUST reveal the source"; #216).
 *
 * Only on keyboard focus (`:focus-visible`), never on a click, which opens the
 * full citation anyway. A caption rather than the panel, because opening the
 * panel on every Tab would reflow the card under the user as they move. It is
 * linked with aria-describedby, so a screen reader announces the source on
 * focus too. Enter still opens the full citation.
 */
export function useFocusSource(sourceId: number | null, unknown: boolean) {
  const [caption, setCaption] = useState<string | null>(null);
  const [id] = useState(() => `fs-${Math.random().toString(36).slice(2, 10)}`);
  const onFocus = (e: FocusEvent) => {
    const el = e.currentTarget as HTMLElement | null;
    if (!el?.matches(":focus-visible")) return;
    if (unknown || sourceId == null) {
      setCaption("Nobody has cited this yet.");
      return;
    }
    setCaption("Loading source…");
    loadSource(sourceId).then(
      (s) => setCaption(
        (isOwnSource(s)
          ? OWN_SOURCE_LABEL
          : `${s.publisher ?? s.title ?? "Source"} · ${sourceKindLabel(s)}`) +
        `${s.retrieved_at ? ` · retrieved ${s.retrieved_at}` : ""} · Enter for the full citation`),
      () => setCaption("The source could not be loaded. Enter to try again."),
    );
  };
  const onBlur = () => setCaption(null);
  return {
    focusProps: { onFocus, onBlur, "aria-describedby": caption ? id : undefined },
    captionEl: caption ? (
      <span class="focus-source" id={id} role="tooltip">{caption}</span>
    ) : null,
  };
}

export function CellProvenance(props: {
  slug: string;
  attributeKey: string;
  /** The spec, so a cited value is worded as its row is (#213). */
  attr?: Attribute | undefined;
  sourceId: number | null;
  competing: number;
  distinct?: number;
  publishers?: number;
  agree?: boolean | undefined;
  /** True when nothing has been cited — a different statement from a conflict. */
  unknown: boolean;
  /** The preferred value is a source's statement that this spec does not apply to this lens (D45). */
  notApplicable?: boolean;
}) {
  const [source, setSource] = useState<Source | null>(null);
  const [history, setHistory] = useState<HistoryValue[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    // A disagreement needs every value, not just the preferred one's citation.
    if (props.competing > 1) {
      fetch(`/api/v1/lenses/${props.slug}/values/${props.attributeKey}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(LOAD_FAILED))))
        .then((d: { values: HistoryValue[] }) => {
          if (live) setHistory(d.values.filter((v) => v.status === "accepted"));
        })
        .catch((e: unknown) => live && setError(e instanceof Error ? e.message : "failed"));
      return () => { live = false; };
    }
    if (props.sourceId === null) return;
    loadSource(props.sourceId)
      .then((s) => live && setSource(s))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : "failed"));
    return () => { live = false; };
  }, [props.slug, props.attributeKey, props.sourceId, props.competing]);

  if (props.unknown) {
    return (
      <p class="prov-empty">
        Nobody has cited this yet. It is a gap in the data, not a zero.
      </p>
    );
  }
  if (error) return <p class="prov-error">{LOAD_FAILED}</p>;

  if (props.competing > 1) {
    if (!history) return <p class="prov-loading">Loading {props.competing} values…</p>;
    return (
      <div class="prov">
        {props.agree === false ? (
          <p class="prov-conflict">
            {history.length} sources disagree. Every value is kept; the site shows
            the one marked preferred.
          </p>
        ) : (
          /* Sources that agree are the strongest cells in the corpus, and used
             to be shown as a conflict (REQ-WEB-006, amended 2026-09-10). */
          <p class="prov-agree">
            {(props.publishers ?? 0) > 1
              ? `${props.publishers} publishers agree on this value.`
              : `Cited ${history.length} times by the same publisher.`}
          </p>
        )}
        <ol class="prov-list">
          {history.map((v) => (
            <li key={v.id} class="prov-item">
              <div class="prov-value">
                <span class="prov-number">
                  {v.not_applicable
                    ? NOT_APPLICABLE
                    : citedValueText(props.attr, v.submitted_value ?? v.value, v.submitted_unit ?? v.unit)}
                </span>
                {v.is_preferred ? <span class="prov-badge">preferred</span> : null}
              </div>
              {v.source ? <SourceLine s={v.source} /> : null}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (!source) return <p class="prov-loading">Loading citation…</p>;
  return (
    <div class="prov">
      {props.notApplicable ? (
        <p class="prov-na">This source says the spec does not apply to this lens.</p>
      ) : null}
      <SourceLine s={source} />
    </div>
  );
}

function SourceLine(props: { s: Source }) {
  const { s } = props;
  const own = isOwnSource(s);
  return (
    <p class="prov-source">
      <a href={citationHref(s.url)} target="_blank" rel="noopener noreferrer">
        {own ? OWN_SOURCE_LABEL : (s.publisher ?? s.title ?? s.url)}
      </a>
      <span class="prov-meta">
        {own ? "" : sourceKindLabel(s)}
        {s.retrieved_at ? `${own ? "" : " · "}retrieved ${s.retrieved_at}` : ""}
      </span>
    </p>
  );
}
